
import React, { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { presenceManager } from '@/lib/presenceManager';
import { supabase } from '@/integrations/supabase/client';

export const UserLoggedInPresence = React.memo(function UserLoggedInPresence() {
  const { user } = useAuth();
  const channelRef = useRef(null);
  const isSubscribedRef = useRef(false);
  const unmountTimerRef = useRef(null);
  
  /**
   * Synchronize user's last seen timestamp with the database
   * @param userId The user ID
   */
  async function syncUserStatus(userId: string): Promise<void> {
    if (!userId) return;
    
    try {
      const { error } = await supabase
        .from('user_status')
        .upsert(
          { user_id: userId, last_seen_at: new Date().toISOString() },
          { onConflict: 'user_id' }
        );
      
      if (error) {
        console.error(`[UserLoggedInPresence] Error updating user status: ${error.message}`);
      } else {
        console.log(`[UserLoggedInPresence] Successfully updated last_seen_at for user ${userId}`);
      }
    } catch (error) {
      console.error('[UserLoggedInPresence] Error synchronizing user status:', error);
    }
  }
  
  useEffect(() => {
    if (!user?.id) return;

    const userId = user.id;
    console.log(`[UserLoggedInPresence] Setting up presence for current user ${userId}`);
    
    // Clear any pending unmount timer
    if (unmountTimerRef.current) {
      clearTimeout(unmountTimerRef.current);
      unmountTimerRef.current = null;
    }
    
    // Set subscribed flag to true since we're mounting
    isSubscribedRef.current = true;
    
    // Only create the channel once and only if not already subscribed
    if (!channelRef.current) {
      // Check if channel is already subscribed or subscription is pending
      if (presenceManager.isChannelSubscribed(userId) || presenceManager.isPendingSubscription(userId)) {
        console.log(`[UserLoggedInPresence] Channel for user ${userId} already subscribed or pending`);
        // Still update the database status since we're considered online now
        syncUserStatus(userId).catch(err => 
          console.error(`[UserLoggedInPresence] Error updating status: ${err}`)
        );
        return;
      }
      
      // Mark subscription as pending to prevent duplicate subscription attempts
      presenceManager.setPendingSubscription(userId, true);
      
      // Use presenceManager for consistency
      channelRef.current = presenceManager.getChannel(userId);
      presenceManager.setChannelSubscribed(userId, false);
      
      channelRef.current
        .on('presence', { event: 'join' }, ({ key, newPresences }) => {
          console.log(`[UserLoggedInPresence] User ${key} joined the presence channel`, newPresences);
        })
        .on('presence', { event: 'sync' }, () => {
          console.log('[UserLoggedInPresence] Presence state synced');
          const state = channelRef.current.presenceState();
          console.log('[UserLoggedInPresence] Current presence state:', state);
        })
        .on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
          console.log(`[UserLoggedInPresence] User with key ${key} left the presence channel`, leftPresences);
          
          // Extract the user_id from the leftPresences for better logging
          if (leftPresences && leftPresences.length > 0) {
            const leftUserId = leftPresences[0].user_id;
            console.log(`[UserLoggedInPresence] User ${leftUserId} left the presence channel`);
          }
        })
        .subscribe(async (status, error) => {
          if (status === 'SUBSCRIBED' && user) {
            console.log(`[UserLoggedInPresence] Successfully subscribed to presence for user ${userId}`);
            presenceManager.setChannelSubscribed(userId, true);
            await channelRef.current.track({
              user_id: userId,
              online_at: new Date().toISOString(),
            });
            
            // Update database status when successfully subscribed
            await syncUserStatus(userId);
          } else if (error) {
            console.error(`[UserLoggedInPresence] Error subscribing to presence for user ${userId}:`, error);
            isSubscribedRef.current = false;
            channelRef.current = null;
          }
        });
    }

    // Periodically update the last_seen_at timestamp while the user is active
    const updateInterval = setInterval(() => {
      if (user?.id && isSubscribedRef.current) {
        syncUserStatus(userId).catch(err => 
          console.error(`[UserLoggedInPresence] Error updating periodic status: ${err}`)
        );
      }
    }, 1 * 60 * 1000); // Update every minute

    // Only clean up when component is truly unmounting or user changes
    return () => {
      if (userId && channelRef.current) {
        console.log(`[UserLoggedInPresence] Cleaning up presence for user ${userId}`);
        // We're not actually releasing the channel here to prevent rapid subscribe/unsubscribe cycles
        // Instead, we'll just set a flag that we're no longer subscribed
        isSubscribedRef.current = false;
        
        // Still update last_seen_at before unmounting
        syncUserStatus(userId).catch(err => 
          console.error(`[UserLoggedInPresence] Error updating status before unmount: ${err}`)
        );
        
        // Clear the update interval
        clearInterval(updateInterval);
        
        // Only release the channel if we're truly unmounting (not just re-rendering)
        unmountTimerRef.current = setTimeout(() => {
          // If after a short delay, the component hasn't been remounted (isSubscribedRef is still false),
          // then we can safely release the channel
          if (!isSubscribedRef.current) {
            console.log(`[UserLoggedInPresence] Truly unmounting, releasing channel for user ${userId}`);
            presenceManager.releaseChannel(userId);
            channelRef.current = null;
          }
        }, 500); // 500ms delay to prevent rapid cycles
      }
    };
  }, [user?.id]); // Only depend on user ID, not the entire user object

  return null;
});

export default UserLoggedInPresence;
